/* RT Sales — shared search core (v2.4.0)
   ============================================================================
   ONE source of truth for query interpretation, loaded on every storefront page
   as assets/rt-search-core.js. The search section, the native form-entry
   handler and predictive search all call into this file — do not copy any of it
   inline anywhere else.

   Pure: no DOM, no Liquid, no globals besides the two it defines. Node can
   require() it directly, so the test harness exercises the shipped bytes.

   Exposes:
     RTQ   — query interpretation (sizes, brands, plans, suggest queries,
             vehicle detection for the rim-finder strip)
     RTNAV — /search URL + fallback-stage state machine

   TWO DIFFERENT BACKENDS, TWO DIFFERENT QUERY SHAPES (measured 2026-08-04):
     /search          matches product TAGS  -> digits form '2756020' (100 hits;
                      the title form '275/60R20' is far broader)
     /search/suggest  does NOT match tags   -> digits form '2756020' returns
                      ZERO; the TITLE form '275/60R20' returns 10.
   So buildPlan() (tag-shaped) drives the results page and suggestQuery()
   (title-shaped) drives predictive search. Feeding plan stages to predictive
   would silently break the single most common query type on this store.
   ============================================================================ */
(function (root, factory) {
  var api = factory();
  root.RTQ = api.RTQ;
  root.RTNAV = api.RTNAV;
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
}(typeof window !== 'undefined' ? window : this, function () {
'use strict';

  var RTQ = (function () {
    'use strict';

    /* [canonical, [aliases]] — longest alias phrase wins; whole-token match only.
       Only high-confidence misspellings; ambiguous words (copper, falcon) are
       deliberately absent. */
    var BRAND_ALIASES = [
      ['Sailun', ['sailun', 'salun', 'sailin', 'saliun', 'sailon']],
      ['BFGoodrich', ['bfgoodrich', 'bfg', 'bf goodrich', 'b f goodrich', 'bfgoodrige', 'bf goodrige']],
      ['Michelin', ['michelin', 'michellin', 'michelen', 'michlin', 'mishelin']],
      ['Bridgestone', ['bridgestone', 'bridgstone', 'bridge stone', 'brigestone', 'bridgeston']],
      ['Goodyear', ['goodyear', 'good year', 'goodyr', 'goodyear']],
      ['Yokohama', ['yokohama', 'yokahama', 'yokohoma', 'yokohama']],
      ['Continental', ['continental', 'contenental', 'continentel']],
      ['Firestone', ['firestone', 'fire stone', 'firston']],
      ['Hankook', ['hankook', 'hancook', 'hankock', 'hankuk']],
      ['Kumho', ['kumho', 'kuhmo', 'khumo']],
      ['Falken', ['falken', 'falkin', 'faulken']],
      ['Nitto', ['nitto', 'nito']],
      ['Nokian', ['nokian', 'nokien', 'nokia']],
      ['Pirelli', ['pirelli', 'pireli', 'perelli', 'pirreli']],
      ['Toyo', ['toyo', 'toyos', 'toya']],
      ['Cooper', ['cooper', 'coopers']],
      ['Westlake', ['westlake', 'west lake']],
      ['Uniroyal', ['uniroyal', 'uni royal']],
      ['Hercules', ['hercules', 'herculese']],
      ['Mickey Thompson', ['mickey thompson', 'mickey thomson', 'mickeythompson']],
      ['Gladiator', ['gladiator', 'gladiater']],
      ['Sceptor', ['sceptor', 'scepter']]
    ];

    /* Service metadata that may trail a size: load index/speed (115t, 123/120s,
       91v), load range (e, lre, 10pr, 10 ply), reinforcement (xl, sl, rf, c). */
    var SERVICE_RE = /^(?:\d{2,3}\/\d{2,3}[a-z]?|\d{2,3}[a-z]?|[a-z]|xl|sl|rf|zr|lr[c-g]|\d{1,2}pr|\d{1,2}ply|ply|3pmsf|m\+s|ms)$/;

    /* Plausibility windows keep bolt patterns, prices, years etc. out. */
    var METRIC = { wMin: 125, wMax: 445, aMin: 25, aMax: 90, dMin: 13, dMax: 26 };
    var FLOAT_ = { lMin: 27, lMax: 44, wMin: 7.5, wMax: 16.5, dMin: 14, dMax: 24 };

    /* Fold fullwidth chars to ascii, unify exotic spaces and multiplication
       signs, decimal commas to dots, lowercase, collapse whitespace. */
    function preNormalize(q) {
      var s = '';
      for (var i = 0; i < q.length; i++) {
        var c = q.charCodeAt(i);
        if (c >= 0xFF01 && c <= 0xFF5E) s += String.fromCharCode(c - 0xFEE0);
        else if (c === 0x3000 || c === 0x00A0 || c === 0x2007 || c === 0x202F || (c >= 0x2000 && c <= 0x200B)) s += ' ';
        else s += q.charAt(i);
      }
      return s.toLowerCase()
        /* Copy-paste debris from listings, invoices and messaging apps:
           (275/65R18), 35x12.50R20", 275-65R18 with an en dash, 275/65R18.
           Without this the size never parses and the search falls back to a
           loose text match. */
        .replace(/[\u2044\u2215]/g, '/')
        .replace(/[\u2010-\u2015]/g, '-')
        .replace(/[()\[\]{}]/g, ' ')
        .replace(/["'\u2018\u2019\u201C\u201D\u2032\u2033]/g, ' ')
        .replace(/[×✕✖⨯]/g, 'x')
        .replace(/(\d),(\d)/g, '$1.$2')
        .replace(/,/g, ' ')
        .replace(/\s+/g, ' ').trim()
        .replace(/[.,;:!?]+$/, '');
    }

    /* A service suffix that may be welded onto a size token: 275/60R20XL,
       35x12.50R20LT, LT275/65R18/E. Split off before confusable repair so the
       'L' in XL is never "corrected" to a 1. */
    var WELDED_SUFFIX_RE = /(?:\/?(?:xl|sl|lt|rf|zr|pr|ply|[cdefgh]))$/;

    /* O→0, I/l→1, but only inside tokens that are otherwise size-shaped
       (digits + separators + optional r/x), so words are never touched. */
    function fixConfusables(s) {
      return s.replace(/[a-z0-9.\/x-]+/g, function (tok) {
        /* 'r2o' / 'r18' — a diameter token trailing its width/ratio */
        if (/^r[0-9oil]{2}$/.test(tok)) return 'r' + tok.slice(1).replace(/o/g, '0').replace(/[il]/g, '1');
        /* peel a welded service suffix and put it back untouched */
        var suffix = '';
        var m = WELDED_SUFFIX_RE.exec(tok);
        if (m && /\d/.test(tok.slice(0, m.index))) { suffix = m[0]; tok = tok.slice(0, m.index); }
        var digits = (tok.match(/\d/g) || []).length;
        var suspects = (tok.match(/[oil]/g) || []).length;
        if (digits < 3 || !suspects || suspects > 2) return tok + suffix;
        var rest = tok.replace(/[0-9oil.\/x-]/g, '').replace(/r/g, '');
        if (rest.length) return tok + suffix;
        return tok.replace(/o/g, '0').replace(/[il]/g, '1') + suffix;
      });
    }

    function inRange(v, min, max) { return v >= min && v <= max; }

    /* Optional welded/trailing service data after a size, e.g. '20XL', '20 C',
       'R18/E', 'R20LT'. Consumed as part of the size match so it neither blocks
       the match nor survives as a mandatory search term. */
    var TAIL = '(?:\\s*\\/?\\s*(?:xl|sl|lt|rf|zr|c|d|e|f|g|h|\\d{1,2}pr|\\d{1,2}ply))?';

    /* Try each size grammar against the string; returns
       {primary, alts, prefix, start, end, kind, parts} or null.
       `parts` carries the numbers so a title-shaped query can be rendered for
       predictive search, which cannot match the digits tag. */
    function findSize(s) {
      var m;
      /* metric, separated: [lt|p]275/60[z]r20[c][xl] — / space . - x */
      var metricRe = new RegExp('(?:^|\\s)(?:lt|p)?(\\d{3})\\s*[\\/\\s.x-]\\s*(\\d{2})(?:\\.\\d{1,2})?\\s*(?:[\\/\\s.x-]\\s*|zr|r)r?\\s*(\\d{2})' + TAIL + '(?=\\s|$)', 'g');
      while ((m = metricRe.exec(s))) {
        var w = +m[1], a = +m[2], d = +m[3];
        if (inRange(w, METRIC.wMin, METRIC.wMax) && inRange(a, METRIC.aMin, METRIC.aMax) && inRange(d, METRIC.dMin, METRIC.dMax)) {
          return { primary: '' + w + a + d, alts: [], prefix: false, start: m.index, end: metricRe.lastIndex,
                   kind: 'metric', parts: { w: w, a: a, d: d } };
        }
      }
      /* metric, attached: 27565R18 / LT27565R18 — digits welded, R separator */
      var attachedRe = new RegExp('(?:^|\\s)(?:lt|p)?(\\d{3})(\\d{2})\\s*(?:zr|r)\\s*(\\d{2})' + TAIL + '(?=\\s|$)', 'g');
      while ((m = attachedRe.exec(s))) {
        var wa = +m[1], aa = +m[2], da = +m[3];
        if (inRange(wa, METRIC.wMin, METRIC.wMax) && inRange(aa, METRIC.aMin, METRIC.aMax) && inRange(da, METRIC.dMin, METRIC.dMax)) {
          return { primary: '' + wa + aa + da, alts: [], prefix: false, start: m.index, end: attachedRe.lastIndex,
                   kind: 'metric', parts: { w: wa, a: aa, d: da } };
        }
      }
      /* flotation: 35x12.50r20 / 33x12.5x20 / 35 12.50 20 / 35x12.50R20LT */
      var floatRe = new RegExp('(?:^|\\s)(\\d{2}(?:\\.\\d{1,2})?)\\s*[x\\/\\s-]\\s*(\\d{1,2}(?:\\.\\d{1,2})?)\\s*(?:[x\\/\\s-]\\s*|zr|r)r?\\s*(\\d{2})' + TAIL + '(?=\\s|$)', 'g');
      while ((m = floatRe.exec(s))) {
        var l = parseFloat(m[1]), wf = parseFloat(m[2]), df = +m[3];
        /* The catalog writes a half-inch diameter with the point removed:
           29.5x8-14 is tagged 29580014 (295 | 800 | 14), 34x8-14 is 3480014.
           Truncating to 29 built 298014 and matched nothing — the size was
           only findable when the customer happened to spell the separator
           exactly as the product TITLE does. */
        var lTag = (l % 1 === 0) ? String(l) : String(Math.round(l * 10));
        if (inRange(l, FLOAT_.lMin, FLOAT_.lMax) && inRange(wf, FLOAT_.wMin, FLOAT_.wMax) && inRange(df, FLOAT_.dMin, FLOAT_.dMax)) {
          /* Catalog tags both 3512520 (12.5 → 125) and 35125020 (12.50 → 1250). */
          var w10 = String(Math.round(wf * 10));
          var w100 = String(Math.round(wf * 100));
          return { primary: '' + lTag + w10 + df, alts: ['' + lTag + w100 + df], prefix: false, start: m.index, end: floatRe.lastIndex,
                   kind: 'flotation', parts: { l: l, w: wf, d: df } };
        }
      }
      /* compact: 2756020 / LT2756020 / P2055516 / 35125020, range-validated */
      var compactRe = new RegExp('(?:^|\\s)(?:lt|p)?(\\d{7,8})' + TAIL + '(?=\\s|$)', 'g');
      while ((m = compactRe.exec(s))) {
        var t = m[1];
        var parts = null, kind = null;
        if (t.length === 7) {
          /* 7 digits are ambiguous: '2756020' is metric 275/60R20, but
             '3512520' is flotation 35x12.5R20 — read as metric it would be
             "351/25R20", and no tire is 351mm wide. Metric section widths are
             always multiples of 5, which separates the two cleanly. */
          if (+t.slice(0, 3) % 5 === 0 &&
              inRange(+t.slice(0, 3), METRIC.wMin, METRIC.wMax) && inRange(+t.slice(3, 5), METRIC.aMin, METRIC.aMax) && inRange(+t.slice(5), METRIC.dMin, METRIC.dMax)) {
            kind = 'metric'; parts = { w: +t.slice(0, 3), a: +t.slice(3, 5), d: +t.slice(5) };
          } else if (inRange(+t.slice(0, 2), FLOAT_.lMin, FLOAT_.lMax) && inRange(+t.slice(2, 5) / 10, FLOAT_.wMin, FLOAT_.wMax) && inRange(+t.slice(5), FLOAT_.dMin, FLOAT_.dMax)) {
            kind = 'flotation'; parts = { l: +t.slice(0, 2), w: +t.slice(2, 5) / 10, d: +t.slice(5) };
          }
        } else if (inRange(+t.slice(0, 2), FLOAT_.lMin, FLOAT_.lMax) && inRange(+t.slice(2, 6) / 100, FLOAT_.wMin, FLOAT_.wMax) && inRange(+t.slice(6), FLOAT_.dMin, FLOAT_.dMax)) {
          kind = 'flotation'; parts = { l: +t.slice(0, 2), w: +t.slice(2, 6) / 100, d: +t.slice(6) };
        }
        /* A compact FLOTATION size must search the same OR-pair the separated
           grammar builds (see the 35x12.50R20 branch above): the catalog tags
           both 3512520 (12.5 -> 125) and 35125020 (12.50 -> 1250) and no single
           encoding covers every product. Without this, someone typing the welded
           form got a strict subset of the results the separated form returns —
           measured 46 vs 51 on 35x12.50R20. Metric compacts have no second
           encoding, so they keep alts: []. */
        if (kind === 'flotation') {
          var cAlts = [];
          var c10 = '' + parts.l + Math.round(parts.w * 10) + parts.d;
          var c100 = '' + parts.l + Math.round(parts.w * 100) + parts.d;
          if (c10 !== t) cAlts.push(c10);
          if (c100 !== t && c100 !== c10) cAlts.push(c100);
          return { primary: t, alts: cAlts, prefix: false, start: m.index, end: compactRe.lastIndex, kind: kind, parts: parts };
        }
        if (kind) return { primary: t, alts: [], prefix: false, start: m.index, end: compactRe.lastIndex, kind: kind, parts: parts };
      }
      /* Legacy/European two-part sizes (195R15, 7.50R16) are deliberately NOT
         synthesized into a digits tag: the catalog has no encoding for them
         (verified — '19515' returns 0, and '75016' matches only by accident,
         those products carry '2057516'). They fall through to the broad stage,
         which returns sensible results without inventing a convention. */
      /* partial: 275 60 / 275/60 — prefix-match all diameters (27560 →
         2756018/20/22 via options[prefix]=last) */
      var partRe = /(?:^|\s)(\d{3})\s*[\/\s.-]\s*(\d{2})(?=\s|$)/g;
      while ((m = partRe.exec(s))) {
        var wp = +m[1], ap = +m[2];
        if (inRange(wp, METRIC.wMin, METRIC.wMax) && inRange(ap, METRIC.aMin, METRIC.aMax)) {
          return { primary: '' + wp + ap, alts: [], prefix: true, start: m.index, end: partRe.lastIndex,
                   kind: 'partial', parts: { w: wp, a: ap } };
        }
      }
      return null;
    }

    /* Render a parsed size the way it appears in PRODUCT TITLES. Predictive
       search matches titles, not tags, so this — not `primary` — is what it
       must be given. Measured: '225/60R18' -> 10 suggestions, '2256018' -> 0. */
    function titleForm(size) {
      if (!size || !size.parts) return null;
      var p = size.parts;
      if (size.kind === 'metric') return p.w + '/' + (p.a < 10 ? '0' + p.a : p.a) + 'R' + p.d;
      if (size.kind === 'flotation') return p.l + 'x' + p.w + 'R' + p.d;
      if (size.kind === 'partial') return p.w + '/' + (p.a < 10 ? '0' + p.a : p.a);
      return null;
    }

    /* Replace recognized brand aliases (longest phrase first) with canonical
       names; returns {tokens, brands}. Token stream only — never substrings. */
    function canonicalizeBrands(tokens) {
      var brands = [];
      var out = [];
      var i = 0;
      outer:
      while (i < tokens.length) {
        for (var len = 3; len >= 1; len--) {
          if (i + len > tokens.length) continue;
          var phrase = tokens.slice(i, i + len).join(' ');
          for (var b = 0; b < BRAND_ALIASES.length; b++) {
            var aliases = BRAND_ALIASES[b][1];
            for (var a = 0; a < aliases.length; a++) {
              if (aliases[a] === phrase && aliases[a].split(' ').length === len) {
                if (brands.indexOf(BRAND_ALIASES[b][0]) === -1) brands.push(BRAND_ALIASES[b][0]);
                i += len;
                continue outer;
              }
            }
          }
        }
        out.push(tokens[i]);
        i++;
      }
      return { tokens: out, brands: brands };
    }

    /* Full interpretation: size (with alternates), canonical brands, leftover
       meaningful words, with service metadata after the size dropped. */
    function interpret(raw) {
      var s = fixConfusables(preNormalize(raw));
      var size = findSize(s);
      var before = size ? s.slice(0, size.start) : s;
      var after = size ? s.slice(size.end) : '';
      var afterTokens = after.split(' ').filter(Boolean);
      /* strip service metadata immediately following the size */
      while (afterTokens.length && SERVICE_RE.test(afterTokens[0])) afterTokens.shift();
      var tokens = [];
      before.split(' ').filter(Boolean).concat(afterTokens).forEach(function (t) {
        /* split alpha-only hyphenated words so 'bf-goodrich' can canonicalize
           (numeric hyphens were already consumed by the size grammar) */
        if (/^[a-z]+(?:-[a-z]+)+$/.test(t)) tokens = tokens.concat(t.split('-'));
        else tokens.push(t);
      });
      tokens = tokens.filter(function (t) { return t !== 'and' && t !== 'lt' && t !== 'p'; });
      var br = canonicalizeBrands(tokens);
      return { size: size, brands: br.brands, words: br.tokens, cleaned: s };
    }

    /* Storefront search no longer honours AND between bare words (measured
       2026-10-01): 'fuel AND blitz' returned 1000 products and 'Sailun AND
       2756020' 1000, 221 of them the wrong size. Field-scoped terms still
       filter: 'title:fuel AND title:blitz' -> the 3 Blitz listings,
       'title:sailun AND tag:2756020' -> 18. So the strict stage scopes every
       word to the title and the size to its tag. Query words and filler are
       dropped; anything that empties out leaves no strict stage. */
    var STRICT_SKIP = ['and', 'or', 'not', 'for', 'the', 'a', 'an', 'with', 'on', 'of', 'to', 'in'];
    /* ART spells its wheels "Replika"; most of our titles say "Replica" (search audit 2026-11-03).
       Either word finds both: '(title:replica OR title:replika)' -> 254 on 2026-11-03, the two
       words alone 211 + 43. */
    var TITLE_ALTS = { replica: ['replica', 'replika'], replika: ['replica', 'replika'] };
    function titleTerms(list) {
      var out = [];
      list.forEach(function (w) {
        String(w).toLowerCase().split(/[\s]+/).forEach(function (t) {
          t = t.replace(/[^a-z0-9.\/-]/g, '').replace(/^[.\/-]+|[.\/-]+$/g, '');
          if (!t || STRICT_SKIP.indexOf(t) !== -1) return;
          var term = TITLE_ALTS[t] ? '(' + TITLE_ALTS[t].map(function (a) { return 'title:' + a; }).join(' OR ') + ')' : 'title:' + t;
          if (out.indexOf(term) === -1) out.push(term);
        });
      });
      return out;
    }

    /* Bounded, ordered list of queries — most precise first, broad last.
       Each stage: {q, prefix}. Size stays the anchor as long as possible. */
    function buildPlan(raw) {
      var p = interpret(raw);
      var stages = [];
      function push(q, prefix) {
        q = q.trim();
        if (!q) return;
        for (var i = 0; i < stages.length; i++) if (stages[i].q === q) return;
        stages.push({ q: q, prefix: !!prefix });
      }
      if (p.size) {
        var extras = p.brands.concat(p.words);
        /* Alternate tag encodings (flotation 12.5 vs 12.50) are OR'd into ONE
           clause — verified supported by storefront search — so a brand filter
           is never dropped just to try the other encoding. */
        var sizeClause = p.size.alts.length
          ? '(' + [p.size.primary].concat(p.size.alts).join(' OR ') + ')'
          : p.size.primary;
        /* A partial size (275 60) has no tag form that still filters — prefix
           matching on tags returns nothing — so it skips the strict stage. */
        var strict = titleTerms(extras);
        if (strict.length && !p.size.prefix) {
          var tagClause = p.size.alts.length
            ? '(' + [p.size.primary].concat(p.size.alts).map(function (t) { return 'tag:' + t; }).join(' OR ') + ')'
            : 'tag:' + p.size.primary;
          push(strict.join(' AND ') + ' AND ' + tagClause, false);
        }
        push(sizeClause, p.size.prefix);
        /* broad last resort: original terms, brands corrected, no AND */
        push(p.brands.concat(p.words).join(' ') + ' ' + p.cleaned.slice(p.size.start, p.size.end).trim(), false);
      } else {
        push(titleTerms(p.brands.concat(p.words)).join(' AND '), false);
        push(p.brands.concat(p.words).join(' '), false);
      }
      return { stages: stages.slice(0, 5), parsed: p };
    }

    /* ---- Predictive search ----------------------------------------------
       /search/suggest matches TITLES, not tags, so the plan's digits form is
       useless there ('2756020' -> 0 suggestions). Build a title-shaped query
       instead: canonical brand names (this is the real win — 'salun 225/60R18'
       returns 0, 'Sailun 225/60R18' returns 10) plus the size as it is written
       in product titles.

       Returns {q, changed}. `changed:false` means we have nothing better than
       what the customer typed, and the caller should issue the ordinary single
       Dawn request with the raw term. */
    function suggestQuery(raw) {
      var p = interpret(raw);
      var title = titleForm(p.size);
      var terms = p.brands.concat(p.words);
      if (title) terms.push(title);
      var q = terms.join(' ').trim();
      if (!q) return { q: String(raw).trim(), changed: false, rawFirst: true };
      var changed = q.toLowerCase() !== String(raw).trim().toLowerCase();

      /* Which query to TRY FIRST.

         Re-spelling a size that the customer already wrote in title shape is a
         coin flip, because Shopify tokenises decimals unpredictably against
         this catalog — measured: '35x12.50R20' returns 9 suggestions but
         '35x12.5R20' returns 5, while '37x13.5R20' returns 9 and '37x13.50R20'
         returns 4. There is no form that wins everywhere, so when the typed
         size already carries a separator we lead with the customer's own text
         and keep the rewrite as the zero-result fallback. We only lead with the
         rewrite when it is unambiguously better: a welded/tag-shaped size
         (which matches nothing here) or a corrected brand spelling. */
      var sizeIsSeparated = false;
      if (p.size) {
        var typed = p.cleaned.slice(p.size.start, p.size.end);
        sizeIsSeparated = /\d\s*[\/.\-x\s]\s*\d/.test(typed);
      }
      var brandCorrected = p.brands.length > 0 &&
        p.brands.some(function (b) { return String(raw).toLowerCase().indexOf(b.toLowerCase()) === -1; });
      var rawFirst = changed && sizeIsSeparated && !brandCorrected;

      return { q: q, changed: changed, rawFirst: rawFirst };
    }

    /* Canonical brand names, for consumers that need the vocabulary (the facet
       engine derives its tire brands from this instead of keeping a copy). */
    function canonicalBrands() {
      return BRAND_ALIASES.map(function (b) { return b[0]; });
    }

    /* ---- vehicle detection (header + /search strip) ----
       Size always wins. A tire/rim brand alone is not a vehicle. "ram" alone
       is not a vehicle; "ram 1500" / "2020 ram" are. Index is loaded via
       loadVehicleIndex (tests) or the browser fetch of fit_search.json. */
    var VEH = null;
    var ALIAS = null;
    var MAKES = null;
    var VEH_BLOCK = {
      cooper: 1, ion: 1, gt: 1, gladiator: 1, fit: 1, ram: 1,
      '1500': 1, '2500': 1, '3500': 1, '200': 1, '300': 1, '500': 1, '911': 1,
      m: 1, z: 1, '86': 1, ls: 1, sl: 1, tc: 1, xb: 1, xd: 1, el: 1, cc: 1
    };
    var VEH_JUNK = /^(rims?|wheels?|tires?|tyres?|for|my|a|the|and|on|with|set|oem|takeoffs?)$/;

    function compactToken(s) {
      return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '');
    }

    function loadVehicleIndex(data) {
      VEH = data && data.models ? data : null;
      ALIAS = Object.create(null);
      MAKES = (data && data.makes) || {};
      if (!VEH) return null;
      VEH.models.forEach(function (row) {
        (row.a || []).forEach(function (al) {
          var k = String(al).toLowerCase();
          if (!ALIAS[k]) ALIAS[k] = [];
          ALIAS[k].push(row);
        });
      });
      return VEH;
    }

    function vehicleIndex() { return VEH; }

    function familyName(rows) {
      var names = rows.map(function (r) { return r.n; });
      var w = names[0].split(/\s+/)[0];
      if (w.length >= 4 && names.every(function (n) { return n.indexOf(w) === 0; })) return w;
      return null;
    }

    function pickRow(rows, year, makeGuess) {
      var list = rows.slice();
      if (makeGuess) {
        var f = list.filter(function (r) { return r.k === makeGuess; });
        if (f.length) list = f;
      }
      if (year) {
        var yf = list.filter(function (r) { return year >= r.y[0] && year <= r.y[1]; });
        if (yf.length) list = yf;
        else return null;
      }
      if (list.length === 1) return list[0];
      list.sort(function (a, b) { return a.n.length - b.n.length; });
      var shortest = compactToken(list[0].n);
      if (shortest && list.every(function (r) { return compactToken(r.n).indexOf(shortest) === 0; })) {
        return list[0];
      }
      var makes = {};
      list.forEach(function (r) { makes[r.k] = 1; });
      if (Object.keys(makes).length === 1) {
        return { k: list[0].k, n: null, family: familyName(list), y: [list[list.length - 1].y[0], list[0].y[1]] };
      }
      return null;
    }

    function finderHref(hit) {
      var q = [];
      if (hit.make) q.push('mk=' + encodeURIComponent(hit.make));
      if (hit.year) q.push('yr=' + encodeURIComponent(String(hit.year)));
      if (hit.model) q.push('md=' + encodeURIComponent(hit.model));
      return '/pages/year-make-model-search' + (q.length ? '?' + q.join('&') : '');
    }

    function vehicleLabel(hit) {
      var bits = [];
      if (hit.year) bits.push(String(hit.year));
      if (hit.model) {
        if (hit.make && hit.model.toLowerCase().indexOf(String(hit.make).toLowerCase()) !== 0) {
          bits.push(hit.make);
        }
        bits.push(hit.model);
      } else if (hit.family) {
        if (hit.make) bits.push(hit.make);
        bits.push(hit.family);
      } else if (hit.make) {
        bits.push(hit.make);
      }
      return bits.join(' ');
    }

    function detectVehicle(raw) {
      if (!VEH || !ALIAS) return null;
      var p = interpret(raw);
      if (p.size) return null;
      var leftover = (p.words || []).filter(function (t) { return t && !VEH_JUNK.test(t); });
      if (p.brands && p.brands.length && leftover.length === 0 && !/\b(?:19|20)\d{2}\b/.test(p.cleaned)) {
        return null;
      }
      var s = p.cleaned;
      if (!s) return null;
      if (/^\d+x\d/.test(s) && !/\b(?:19|20)\d{2}\b/.test(s)) return null;

      var year = null;
      var ym = s.match(/\b((?:19|20)\d{2})\b/);
      if (ym) {
        var y = +ym[1];
        if (y >= 1990 && y <= 2027) year = y;
      }

      var tokens = s.replace(/\b(?:19|20)\d{2}\b/g, ' ').split(/\s+/).filter(function (t) {
        return t && !VEH_JUNK.test(t);
      });
      if (!tokens.length && !year) return null;

      var makeGuess = null;
      var len;
      var hitRow = null;
      var family = null;

      /* Model aliases on the FULL token string first — "ram 1500" must beat
         consuming "ram" as a make, so 2008 vs 2020 land on Dodge vs RAM. */
      if (tokens.length) {
        for (len = Math.min(4, tokens.length); len >= 1 && !hitRow && !family; len--) {
          var ph0 = tokens.slice(0, len).join(' ');
          var key0 = ph0.toLowerCase();
          var ck0 = compactToken(ph0);
          if (len === 1 && (VEH_BLOCK[key0] || VEH_BLOCK[ck0])) continue;
          var rows0 = ALIAS[key0] || ALIAS[ck0];
          if (!rows0 || !rows0.length) continue;
          var picked0 = pickRow(rows0, year, null);
          if (picked0 && picked0.n) hitRow = picked0;
          else if (picked0) family = picked0;
        }
      }

      if (!hitRow && !family) {
        for (len = 2; len >= 1; len--) {
          if (tokens.length < len) continue;
          var phrase = tokens.slice(0, len).join(' ');
          var mk = MAKES[phrase] || MAKES[compactToken(phrase)];
          if (mk) { makeGuess = mk; tokens = tokens.slice(len); break; }
        }
      }

      if (!hitRow && !family && tokens.length) {
        for (len = Math.min(4, tokens.length); len >= 1 && !hitRow && !family; len--) {
          var ph = tokens.slice(0, len).join(' ');
          var key = ph.toLowerCase();
          var ck = compactToken(ph);
          if (len === 1 && !makeGuess && (VEH_BLOCK[key] || VEH_BLOCK[ck])) continue;
          var rows = ALIAS[key] || ALIAS[ck];
          if (!rows || !rows.length) continue;
          var picked = pickRow(rows, year, makeGuess);
          if (picked && picked.n) hitRow = picked;
          else if (picked) family = picked;
        }
        if (!hitRow && !family && tokens.length === 1) {
          var pref = compactToken(tokens[0]);
          if (pref.length >= 5 && !VEH_BLOCK[pref]) {
            var prefRows = VEH.models.filter(function (r) {
              return compactToken(r.n).indexOf(pref) === 0;
            });
            if (prefRows.length) {
              var picked2 = pickRow(prefRows, year, makeGuess);
              if (picked2 && picked2.n) hitRow = picked2;
              else if (picked2) family = picked2;
            }
          }
        }
      }

      if (!hitRow && !family) {
        if (year && makeGuess) {
          var only = { make: makeGuess, model: null, year: year, family: null };
          only.label = vehicleLabel(only);
          only.href = finderHref(only);
          return only;
        }
        return null;
      }

      var make = hitRow ? hitRow.k : family.k;
      var model = hitRow ? hitRow.n : null;
      var fam = hitRow ? null : (family.family || null);
      if (hitRow && year && (year < hitRow.y[0] || year > hitRow.y[1])) year = null;
      var hit = { make: make, model: model, year: year, family: fam };
      hit.label = vehicleLabel(hit);
      hit.href = finderHref(hit);
      if (!hit.label) return null;
      return hit;
    }

    return { preNormalize: preNormalize, fixConfusables: fixConfusables, findSize: findSize,
             canonicalizeBrands: canonicalizeBrands, interpret: interpret, buildPlan: buildPlan,
             titleForm: titleForm, suggestQuery: suggestQuery, canonicalBrands: canonicalBrands,
             loadVehicleIndex: loadVehicleIndex, vehicleIndex: vehicleIndex,
             detectVehicle: detectVehicle, finderHref: finderHref };
  })();


  /* ---- RTNAV: URL / stage decision logic ----

     URL contract:
       q                  the query actually sent to Shopify (a plan stage)
       rtq                the customer's original, untouched text
       rtst               index of the active stage (bounds the fallback walk)
       type=product       always — this is a tires/rims catalog search
       options[prefix]    present IFF the active stage wants prefix matching

     Loop safety rests on two properties, both asserted in the tests:
       (1) canonicalisation is idempotent — decide(decide(x)) redirects once,
           never twice; and
       (2) a stage advance strictly increases rtst, which is bounded by the
           plan length (max 5). */
  var RTNAV = (function () {
    'use strict';

    /* Only these keys take part in the redirect decision. Everything else
       (sort_by, locale params, utm_*) is carried through untouched, so
       parameter ORDER can never trigger a redirect loop. */
    var CONTROLLED = ['q', 'type', 'options[prefix]', 'rtq', 'rtst'];

    function toObject(sp) {
      var o = {};
      sp.forEach(function (v, k) { o[k] = v; });
      return o;
    }

    /* Which stage does this q correspond to? -1 when the URL no longer matches
       any stage of the plan (hand-edited, or the plan changed since the link
       was bookmarked). */
    function stageIndexOf(plan, q) {
      for (var i = 0; i < plan.stages.length; i++) {
        if (plan.stages[i].q.toLowerCase() === String(q).toLowerCase()) return i;
      }
      return -1;
    }

    function parseStageIndex(raw) {
      if (raw === null || raw === undefined || !/^\d+$/.test(String(raw).trim())) return null;
      var n = parseInt(raw, 10);
      return (isNaN(n) || n < 0) ? null : n;
    }

    /* The params this URL *should* have for the given stage. */
    function desiredParams(current, stage, original, idx, stageChanged) {
      var d = toObject(current);
      d['q'] = stage.q;
      d['type'] = 'product';
      d['rtq'] = original;
      d['rtst'] = String(idx);
      if (stage.prefix) d['options[prefix]'] = 'last';
      else delete d['options[prefix]'];
      /* A different result set invalidates the page cursor. sort_by is a
         deliberate customer choice and survives. */
      if (stageChanged) delete d['page'];
      return d;
    }

    function differs(current, desired) {
      for (var i = 0; i < CONTROLLED.length; i++) {
        var k = CONTROLLED[i];
        var cur = current.has(k) ? current.get(k) : null;
        var want = Object.prototype.hasOwnProperty.call(desired, k) ? desired[k] : null;
        if (cur !== want) return true;
      }
      /* page is only controlled when we drop it */
      if (current.has('page') && !Object.prototype.hasOwnProperty.call(desired, 'page')) return true;
      return false;
    }

    function serialize(obj) {
      var sp = new URLSearchParams();
      Object.keys(obj).forEach(function (k) { sp.set(k, obj[k]); });
      return sp.toString();
    }

    /* decide(search, zeroResults) -> {redirect, search, stage, index, original, reason}
       `search` in = window.location.search (or any query string).
       `zeroResults` = true when the server rendered 0 results for this URL. */
    function decide(search, zeroResults) {
      var current = new URLSearchParams(search || '');
      var q = current.get('q');
      if (!q) return { redirect: false, reason: 'no-query', index: null, original: null };

      var rtq = current.get('rtq');
      var isFresh = (rtq === null || rtq === '');
      var original = isFresh ? q : rtq;
      var plan = RTQ.buildPlan(original);
      if (!plan.stages.length) return { redirect: false, reason: 'empty-plan', index: null, original: original };

      var idx, matched, reason;
      if (isFresh) {
        /* A newly typed query ALWAYS enters at stage 0. It must not be matched
           against later stages: a partial size like '275 60' is textually
           identical to its own broad fallback stage, and matching would skip
           the precise stage the customer deserves first. */
        idx = 0;
        matched = (plan.stages[0].q.toLowerCase() === String(q).toLowerCase()) ? 0 : -1;
        reason = 'canonical';
      } else {
        idx = parseStageIndex(current.get('rtst'));
        matched = stageIndexOf(plan, q);
        /* Trust the URL's q over a bad rtst: a malformed, out-of-range or stale
           index recovers to whichever stage the query actually IS, and only
           falls back to stage 0 when the query matches no stage at all. */
        reason = 'canonical';
        if (matched === -1) {
          /* q belongs to no stage (hand-edited q, or the plan changed since
             this link was made) — restart the plan from stage 0. */
          idx = 0;
          reason = 'replanned';
        } else if (idx === null || idx >= plan.stages.length || matched !== idx) {
          idx = matched;
          reason = 'recovered-state';
        }
      }

      var startIdx = idx;
      /* Advance only when this exact URL is the one that returned nothing. */
      if (zeroResults && matched === idx && idx + 1 < plan.stages.length) {
        idx = idx + 1;
        reason = 'advance-empty';
      }

      var stage = plan.stages[idx];
      var stageChanged = (idx !== startIdx) || (matched !== idx);
      var desired = desiredParams(current, stage, original, idx, stageChanged);

      if (!differs(current, desired)) {
        return { redirect: false, reason: 'already-canonical', index: idx, original: original, stage: stage, plan: plan };
      }
      return {
        redirect: true, search: serialize(desired), reason: reason,
        index: idx, original: original, stage: stage, plan: plan
      };
    }

    /* Params a native search FORM should submit, so an ordinary search reaches
       canonical stage 0 in ONE navigation instead of loading the raw query and
       then being redirected by decide().

       Pure: takes the typed text plus any existing form/URL params, returns the
       full param object. `journey` is the analytics id minted at submit time.
       Returns null when there is nothing to submit. */
    function formParams(rawInput, existing, journey) {
      var raw = String(rawInput == null ? '' : rawInput).trim();
      if (!raw) return null;
      var plan = RTQ.buildPlan(raw);
      if (!plan.stages.length) return null;
      var stage = plan.stages[0];

      var out = {};
      if (existing) {
        Object.keys(existing).forEach(function (k) { out[k] = existing[k]; });
      }
      /* stage-owned + stale keys are always recomputed, never inherited */
      delete out['page'];
      delete out['options[prefix]'];
      out['q'] = stage.q;
      out['rtq'] = raw;
      out['rtst'] = '0';
      out['type'] = 'product';
      if (stage.prefix) out['options[prefix]'] = 'last';
      if (journey) out['rtj'] = journey;
      return out;
    }

    return { decide: decide, stageIndexOf: stageIndexOf, parseStageIndex: parseStageIndex,
             formParams: formParams, CONTROLLED: CONTROLLED };
  })();

return { RTQ: RTQ, RTNAV: RTNAV };
}));

/* Browser-only: load the vehicle alias table + prefill the finder from ?mk=&yr=&md=.
   Not part of the UMD factory — Node tests require() the factory only. */
(function () {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  var VEH_VER = '2026-08-19a';

  function assetDir() {
    var scripts = document.getElementsByTagName('script');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].src || '';
      var n = src.indexOf('rt-search-core.js');
      if (n !== -1) return src.slice(0, n);
    }
    return '/cdn/shop/t/21/assets/';
  }

  function notify() {
    try { document.dispatchEvent(new Event('rt-fit-search-ready')); } catch (e) {}
    if (typeof window.rtPaintSearchVehicle === 'function') window.rtPaintSearchVehicle();
  }

  if (window.RTQ && typeof RTQ.loadVehicleIndex === 'function' && !RTQ.vehicleIndex()) {
    var url = assetDir() + 'fit_search.json?v=' + VEH_VER;
    fetch(url).then(function (r) { return r.ok ? r.json() : null; }).then(function (data) {
      if (!data) return;
      RTQ.loadVehicleIndex(data);
      notify();
    }).catch(function () {});
  }

  var path = (window.location && location.pathname) || '';
  if (path.indexOf('year-make-model-search') === -1) return;
  var P = new URLSearchParams(location.search);
  var mk = P.get('mk'), yr = P.get('yr'), md = P.get('md');
  if (!mk && !yr && !md) return;

  function waitSet(id, val, cb) {
    var n = 0;
    var t = setInterval(function () {
      n++;
      var el = document.getElementById(id);
      if (!el || el.disabled) { if (n > 80) clearInterval(t); return; }
      if (!val) {
        if (el.options.length > 1) { clearInterval(t); if (cb) cb(); }
        if (n > 80) clearInterval(t);
        return;
      }
      var ok = false;
      for (var i = 0; i < el.options.length; i++) if (el.options[i].value === val) ok = true;
      if (!ok) { if (n > 80) clearInterval(t); return; }
      if (el.value === val) { clearInterval(t); if (cb) cb(); return; }
      el.value = val;
      el.dispatchEvent(new Event('change', { bubbles: true }));
      clearInterval(t);
      if (cb) cb();
    }, 80);
  }

  function start() {
    if (!mk) return;
    waitSet('rtfind-make', mk, function () {
      if (!yr) return;
      waitSet('rtfind-year', yr, function () {
        if (!md) return;
        waitSet('rtfind-model', md);
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
